import moment from 'moment';
import * as React from 'react';
import {
  List,
  Edit,
  Create,
  SimpleForm,
  TextField,
  EditButton,
  TextInput,
  DateField,
  useEditContext,
  required,
  useTranslate,
  useNotify,
  useRefresh,
  TopToolbar,
  CreateButton,
  Button,
} from 'react-admin';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

import EmptyList from '../components/misc/EmptyList';
import SelectUserByEmailInput from '../components/form/SelectUserByEmailInput';
import { TABBED_LIST_SX } from '../components/misc/tabbedListLayout';
import ListQSearchBar from '../components/misc/ListQSearchBar';
import ResizableDatagrid from '../components/table/ResizableDatagrid';
import { apiPost } from '../services/apiClient';

const entity = 'affiliates';

const PullAffiliatesButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = React.useState(false);

  const handleClick = async () => {
    if (loading) return;
    const ok = window.confirm(
      translate('affiliates.pull_confirm', {
        _: 'Firebase’deki tüm affiliate kayıtları Postgres’e aktarılsın mı?',
      }),
    );
    if (!ok) return;

    setLoading(true);
    try {
      const res = await apiPost('affiliates/pull-from-firebase', {});
      const c = res?.counts || {};
      notify(
        translate('affiliates.pull_ok', {
          _: 'Pull OK: {{affiliates}} affiliate, {{users}} kullanıcı',
          affiliates: c.affiliates ?? 0,
          users: c.users ?? 0,
        }),
        { type: 'success' },
      );
      if (c.errors) {
        notify(
          translate('affiliates.pull_partial', {
            _: '{{count}} kayıtta hata (detay konsolda)',
            count: c.errors,
          }),
          { type: 'warning' },
        );
        if (res?.errors?.length) console.warn('affiliate pull errors', res.errors);
      }
      refresh();
    } catch (err) {
      console.error(err);
      notify(
        translate('affiliates.pull_error', {
          _: 'Pull hata: {{message}}',
          message: err?.message || String(err),
        }),
        { type: 'error' },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      label={
        loading
          ? translate('affiliates.pulling', { _: 'Çekiliyor…' })
          : translate('affiliates.pull_firebase', {
              _: 'Firebase’den Çek (Postgres)',
            })
      }
      onClick={handleClick}
      disabled={loading}
      sx={{
        backgroundColor: '#b45309',
        color: '#fff',
        '&:hover': { backgroundColor: '#92400e' },
        '&.Mui-disabled': { color: '#fff', opacity: 0.7 },
      }}
    >
      <CloudDownloadIcon />
    </Button>
  );
};

const AffiliatesListActions = () => (
  <TopToolbar>
    <PullAffiliatesButton />
    <CreateButton />
  </TopToolbar>
);

export const AffiliatesList = (props) => {
  const translate = useTranslate();

  return (
    <List
      {...props}
      empty={<EmptyList entity={entity} />}
      title={translate('affiliates.title', { _: 'Satış Ortakları (Affiliates)' })}
      exporter={false}
      actions={<AffiliatesListActions />}
      sx={TABBED_LIST_SX}
    >
      <ListQSearchBar placeholderKey="affiliates.search_placeholder" />
      <ResizableDatagrid storageKey="gk-datagrid-widths:affiliates">
        <TextField source="name" label={translate('affiliates.name', { _: 'İsim' })} />
        <TextField source="code" label={translate('affiliates.code', { _: 'Ortaklık Kodu' })} />
        <DateField
          source="createdate"
          label={translate('affiliates.created', { _: 'Oluşturulma Tarihi' })}
        />
        <EditButton />
      </ResizableDatagrid>
    </List>
  );
};

export const AffiliatesCreate = (props) => {
  const translate = useTranslate();
  const code = React.useMemo(() => `AF${moment().format('x')}`, []);

  return (
    <Create
      title={translate('affiliates.new_affiliate', { _: 'Yeni Satış Ortağı' })}
      {...props}
      redirect="list"
    >
      <SimpleForm defaultValues={{ id: code, code }} redirect="list">
        <h3>{translate('affiliates.details', { _: 'Satış Ortağı Detayları' })}</h3>
        <TextInput
          source="name"
          label={translate('affiliates.name', { _: 'İsim / Şirket Adı' })}
          fullWidth
          validate={required()}
        />
        <TextInput
          source="code"
          label={translate('affiliates.code', { _: 'Ortaklık Kodu' })}
          fullWidth
          disabled
        />

        <h3>{translate('promoters.user', { _: 'Kullanıcı' })}</h3>
        <SelectUserByEmailInput source="user" required />
      </SimpleForm>
    </Create>
  );
};

export const AffiliatesEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit
      title={translate('affiliates.edit_affiliate', { _: 'Satış Ortağını Düzenle' })}
      {...props}
    >
      <SimpleForm redirect={`/${entity}`}>
        <EditForm />
      </SimpleForm>
    </Edit>
  );
};

const EditForm = () => {
  const translate = useTranslate();
  const { record, isLoading } = useEditContext();
  if (isLoading) return null;

  return (
    <React.Fragment>
      <h3>{translate('affiliates.details', { _: 'Satış Ortağı Detayları' })}</h3>
      <TextInput
        source="name"
        label={translate('affiliates.name', { _: 'İsim / Şirket Adı' })}
        fullWidth
      />
      <TextInput
        source="code"
        label={translate('affiliates.code', { _: 'Ortaklık Kodu' })}
        fullWidth
        disabled
      />

      <h3>{translate('promoters.user', { _: 'Kullanıcı' })}</h3>
      <SelectUserByEmailInput source="user" record={record} />
    </React.Fragment>
  );
};
